import { ProgressBar, TestAnswerList, TestCardsFrame } from '@components';
import { useModuleData, useScrollToTop } from '@hooks';
import { Flex } from '@styles';
import { func, object } from 'prop-types';
import React, { useEffect, useState } from 'react';

const TestLayout = ({ onFinish, openRankModal, setRankModalContent, test }) => {
  const [areAnswersShown, setAreAnswersShown] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [isTestPassed, setIsTestPassed] = useState(false);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [validatedAnswers, setValidatedAnswers] = useState(null);
  const [isValidated, setIsValidated] = useState(false);

  const { createTestProgress, isOffline, updateTestProgress, validateTest } =
    useModuleData({ moduleTest: test });

  const isTestFinished = answers?.length === test?.total_steps;

  const handleShowAnswers = () => {
    setAreAnswersShown(true);
  };

  const handleAnswer = (id, option) => async () => {
    if (currentStep === 0) {
      await createTestProgress({ testId: test?.id });
    }
    await updateTestProgress({
      passedStep: {
        chosenOptionId: option?.id,
        id,
      },
      testId: test?.id,
    });
    const newAnswers = [...answers, { optionId: option?.id, testStepId: id }];
    setAnswers(newAnswers);
    if (newAnswers?.length !== test?.total_steps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleValidateTest = async () => {
    const requestParams = {
      data: {
        testAnswers: answers,
      },
      id: test?.id,
    };

    const validatedTestResult = isOffline
      ? await validateTest(requestParams)
      : await validateTest(requestParams).unwrap();
    const data = validatedTestResult?.data;

    setIsTestPassed(data?.is_passed);
    setCorrectAnswers(data?.correct_answers);
    setValidatedAnswers(data?.validated_answers);

    if (data?.is_passed) {
      setRankModalContent(data?.unlocked_entities);
      if (data?.unlocked_entities) {
        openRankModal();
      }
    }

    setTimeout(() => {
      setIsValidated(true);
    }, 200);
  };

  const handleResetTest = () => {
    setAreAnswersShown(false);
    setAnswers([]);
    setCurrentStep(0);
    setIsTestPassed(false);
    setCorrectAnswers(0);
    setValidatedAnswers(null);
    setIsValidated(false);
  };

  const handleGoBack = () => {
    onFinish();
  };

  useScrollToTop();

  useEffect(() => {
    if (isTestFinished) {
      handleValidateTest();
    }
  }, [answers, currentStep]);

  return (
    <Flex flexDirection="column">
      <ProgressBar
        currentStep={currentStep}
        items={isTestFinished ? validatedAnswers : test?.steps}
        withCorrection={isTestFinished}
      />
      {areAnswersShown ? (
        <TestAnswerList
          handleGoBack={handleGoBack}
          handleReset={handleResetTest}
          isTestFinished={isTestFinished}
          isTestPassed={isTestPassed}
          validatedAnswers={validatedAnswers}
        />
      ) : (
        <TestCardsFrame
          correctAnswers={correctAnswers} // TO DO validation
          data={test?.steps}
          handleGoBack={handleGoBack}
          handleReset={handleResetTest}
          handleShowAnswers={handleShowAnswers}
          isTestFinished={isTestFinished}
          isTestPassed={isTestPassed}
          isValidated={isValidated}
          onLeftButtonPress={handleAnswer(
            test?.steps?.[currentStep]?.id,
            test?.steps?.[currentStep]?.options?.[1]
          )}
          onRightButtonPress={handleAnswer(
            test?.steps?.[currentStep]?.id,
            test?.steps?.[currentStep]?.options?.[0]
          )}
          withCornerNumbers
        />
      )}
    </Flex>
  );
};

TestLayout.propTypes = {
  onFinish: func.isRequired,
  openRankModal: func.isRequired,
  setRankModalContent: func.isRequired,
  test: object.isRequired,
};

export default TestLayout;
